<template>
  <header class="topbar">
    <div class="header-btn__wrap"
         v-if="homeInfo?.homeContent?.platformIntro">
      <a class="header-btn"
         :href="homeInfo?.homeContent?.platformIntro?.link || ''"
         target="_blank">
        {{ homeInfo?.homeContent?.platformIntro?.title || '' }}
        <svg class="btn__icon"
             fill="none"
             stroke="currentColor"
             viewBox="0 0 24 24"
             stroke-width="2.5">
          <path stroke-linecap="round"
                stroke-linejoin="round"
                d="M13 7l5 5m0 0l-5 5m5-5H6"></path>
        </svg>
      </a>
    </div>

    <div class="section-wrap">
      <router-link class="brand"
                   style="flex: 0 1 auto;"
                   to="/home">
        <img v-if="homeInfo?.homeContent?.pcImage1"
             class="topbar-logo"
             :src="homeInfo?.homeContent?.pcImage1"
             alt="logo" />
      </router-link>

      <div class="nav-scroll-wrap topbar-desktop"
           :class="{ 'nav-scroll-pad': state.navCanScrollLeft || state.navCanScrollRight }">
        <button v-show="state.navCanScrollLeft"
                type="button"
                class="nav-scroll-btn nav-scroll-l"
                aria-label="scroll menu left"
                @click="scrollNavMenu(-200)">
          <i class="el-icon-arrow-left"></i>
        </button>

        <el-menu ref="navMenu"
                 class="t1-nav t1-menu"
                 style="flex: auto; overflow-x: auto;"
                 mode="horizontal"
                 :default-active="activeNavIndex"
                 text-color="hsl(from var(--primary-color) h s l/.6)"
                 active-text-color="var(--primary-color)"
                 background-color="var(--background-color)"
                 @select="onNavSelect">
          <template v-for="(item, i) in navList">
            <el-submenu v-if="hasNavChildren(item)"
                        :key="'nav-sub-' + i"
                        :index="navIdx(i)"
                        :show-timeout="150"
                        :hide-timeout="0"
                        popper-class="menu-popup__wrap"
                        popper-append-to-body
                        style="background-color: var(--background-color)!important">
              <template slot="title">
                {{ item.label }}
              </template>
              <!-- 直接渲染 el-menu-item，避免 functional 子组件导致水平菜单下拉不出现 -->
              <template v-for="(child, ci) in item.children">
                <el-submenu v-if="hasNavChildren(child)"
                            :key="'nav-nest-' + i + '-' + ci"
                            :index="navIdx(ci, navIdx(i))"
                            popper-class="menu-popup__wrap"
                            popper-append-to-body>
                  <template slot="title">
                    {{ child.label }}
                  </template>
                  <el-menu-item v-for="(leaf, li) in child.children"
                                :key="'leaf-' + i + ci + li"
                                :index="navIdx(li, navIdx(ci, navIdx(i)))">
                    {{ leaf.label }}
                  </el-menu-item>
                </el-submenu>
                <el-menu-item v-else
                              :key="child.href || 'nav-ch-' + i + '-' + ci"
                              :index="navIdx(ci, navIdx(i))">
                  <a v-if="isExtNav(child.href)"
                     class="nav-btn"
                     :href="child.href"
                     target="_blank"
                     rel="noopener noreferrer"
                     @click.stop>
                    {{ child.label }}
                  </a>
                  <template v-else>
                    {{ child.label }}
                  </template>
                </el-menu-item>
              </template>
            </el-submenu>
            <el-menu-item v-else
                          :key="item.href || 'nav-item-' + i"
                          :index="navIdx(i)">
              <a v-if="isExtNav(item.href)"
                 class="nav-btn"
                 :href="item.href"
                 target="_blank"
                 rel="noopener noreferrer"
                 @click.stop>
                {{ item.label }}
              </a>
              <template v-else>
                {{ item.label }}
              </template>
            </el-menu-item>
          </template>
        </el-menu>

        <button v-show="state.navCanScrollRight"
                type="button"
                class="nav-scroll-btn nav-scroll-r"
                aria-label="scroll menu right"
                @click="scrollNavMenu(200)">
          <i class="el-icon-arrow-right"></i>
        </button>
      </div>

      <div class="platform-wrap topbar-desktop"
           style="flex: 0 1 auto;">
        <SelectPurchasingPlatform v-show="(!isMobile && homeInfo.showPlatform == 'Y') || (isMobile && homeInfo.mobileShowPlatform == 'Y')"
                                  :expert="expertConfig.expert"
                                  @handleChangeExpert="onChangeExpert" />

        <el-input v-model="state.searchValue"
                  class="search-input--image"
                  clearable
                  prefix-icon="el-icon-search"
                  placeholder="Search"
                  autocomplete="off"
                  @keyup.enter.native="goGoodsSearch">
          <template #append>
            <!-- 图片搜索按钮 -->
            <ImageSearchBtn />
          </template>
        </el-input>
      </div>

      <!-- 小屏展开按钮 -->
      <button class="topbar-mobile-btn"
              type="button"
              aria-label="open menu"
              @click="state.drawerOpen = true">
        <i class="el-icon-s-fold"></i>
      </button>

      <!-- 主抽屉 -->
      <el-drawer :visible.sync="state.drawerOpen"
                 direction="rtl"
                 :with-header="false"
                 size="86%"
                 custom-class="t1-mobile-drawer"
                 append-to-body>
        <div class="t1-drawer-inner"
             @wheel.stop>
          <div class="t1-drawer-head">
            <router-link class="brand"
                         to="/home"
                         @click.native="state.drawerOpen = false">
              <img v-if="homeInfo?.homeContent?.pcImage1"
                   class="topbar-logo"
                   :src="homeInfo?.homeContent?.pcImage1"
                   alt="logo" />
            </router-link>
            <button class="t1-drawer-close"
                    type="button"
                    aria-label="close menu"
                    @click="state.drawerOpen = false">
              <i class="el-icon-circle-close"></i>
            </button>
          </div>

          <div ref="mainNavBox"
               class="t1-drawer-nav-box">
            <nav class="t1-drawer-nav"
                 @scroll.passive="onDrawerNavScroll">
              <span v-for="item in navList"
                    :key="item.href"
                    class="t1-drawer-nav-item">
                <a v-if="item.href.startsWith('http')"
                   class="t1-drawer-link"
                   :href="item.href"
                   target="_blank">
                  {{ item.label }}
                </a>
                <!-- 有子级：整行点击进入二级抽屉 -->
                <button v-else-if="hasNavChildren(item)"
                        type="button"
                        class="t1-drawer-link"
                        @click="openSubDrawer(item)">
                  {{ item.label }}
                  <i class="el-icon-arrow-right"></i>
                </button>
                <router-link v-else
                             class="t1-drawer-link"
                             :to="item.href"
                             @click.native="onDrawerNavClick">
                  {{ item.label }}
                </router-link>
              </span>
            </nav>
            <div class="t1-drawer-bar"
                 aria-hidden="true">
              <div class="t1-drawer-thumb"></div>
            </div>
          </div>

          <div style="padding-right: 16px; display: flex; flex-direction: column; row-gap: 12px;">
            <SelectPurchasingPlatform class="topbar__platform-wrap"
                                      v-show="(!isMobile && homeInfo.showPlatform == 'Y') || (isMobile && homeInfo.mobileShowPlatform == 'Y')"
                                      :expert="expertConfig.expert"
                                      @handleChangeExpert="onChangeExpert" />
            <el-input v-model="state.searchValue"
                      class="search-input--image"
                      clearable
                      prefix-icon="el-icon-search"
                      placeholder="Search"
                      autocomplete="off"
                      @keyup.enter.native="goGoodsSearch">
              <template #append>
                <!-- 图片搜索按钮 -->
                <ImageSearchBtn />
              </template>
            </el-input>
          </div>
        </div>
      </el-drawer>

      <!-- 二级分类：从左侧滑出 -->
      <el-drawer :visible.sync="state.subDrawerOpen"
                 direction="rtl"
                 :with-header="false"
                 size="86%"
                 custom-class="t1-mobile-drawer t1-sub-drawer"
                 append-to-body>
        <div class="t1-drawer-inner"
             @wheel.stop>
          <div class="t1-sub-head"
               @click="closeSubDrawer">
            <button type="button"
                    class="t1-sub-back"
                    aria-label="close submenu">
              <i class="el-icon-arrow-left"></i>
            </button>
            <span class="t1-sub-title">
              {{ state.subNavItem?.label }}
            </span>
          </div>

          <div ref="subNavBox"
               class="t1-drawer-nav-box">
            <nav class="t1-drawer-nav"
                 @scroll.passive="onDrawerNavScroll">
              <span v-for="(child, ci) in state.subNavItem?.children || []"
                    :key="child.href || 'sub-' + ci"
                    class="t1-drawer-nav-item">
                <a v-if="isExtNav(child.href)"
                   class="t1-drawer-link"
                   :href="child.href"
                   target="_blank"
                   rel="noopener noreferrer"
                   @click="closeAllDrawers">
                  <img v-if="child.categoryCover"
                       :src="child.categoryCover"
                       class="t1-drawer-link-img" />
                  <img v-else
                       src="@/assets/images/no-picture.svg"
                       class="t1-drawer-link-img" />
                  {{ child.label }}
                </a>
                <router-link v-else
                             class="t1-drawer-link"
                             :to="child.href"
                             @click.native="closeAllDrawers">
                  <img v-if="child.categoryCover"
                       :src="child.categoryCover"
                       class="t1-drawer-link-img" />
                  <img v-else
                       src="@/assets/images/no-picture.svg"
                       class="t1-drawer-link-img" />
                  {{ child.label }}
                </router-link>
              </span>
            </nav>
            <div class="t1-drawer-bar"
                 aria-hidden="true">
              <div class="t1-drawer-thumb"></div>
            </div>
          </div>
        </div>
      </el-drawer>
    </div>
  </header>
</template>

<script>
import SelectPurchasingPlatform from '@/components/selectPurchasingPlatform.vue'
import productApi from '@/api/product'
import { getGoodsCategoryList } from '@/api/cache'
import ImageSearchBtn from '@/components/ImageSearchBtn.vue'

export default {
  name: 'HomeTopbar',

  components: {
    SelectPurchasingPlatform,
    ImageSearchBtn
  },

  props: {
    isMobile: {
      type: Boolean,
      default: false
    },
    homeInfo: {
      type: Object,
      default: () => ({})
    },
    expertConfig: {
      type: Object,
      default: () => ({})
    },
    defaultMenuList: {
      type: Array,
      default: () => []
    },
    isQequest: {
      type: Boolean,
      default: false
    },
    platformIntro: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      state: {
        drawerOpen: false,
        subDrawerOpen: false,
        subNavItem: null,
        searchValue: '',
        // 顶部导航，来自 getHomeConfig.menuList
        menuList: [],
        // 商品分类树（getGoodsCategoryList）
        goodsCategoryList: [],
        // 顶栏菜单横向溢出时显示左右滚动箭头
        navCanScrollLeft: false,
        navCanScrollRight: false
      }
    }
  },

  computed: {
    expertId() {
      return this.expertConfig?.expert?.id || this.homeInfo?.expert?.id || ''
    },

    // 菜单接口 + 分类接口
    navList() {
      const arr = [
        { href: '/home', label: 'HOME' },
        { href: '/goods', label: 'ALL PRODUCTS' },
        ...this.state.menuList,
        ...this.buildCatNavTree(this.state.goodsCategoryList)
      ]
      if (this.homeInfo?.excelUrl) {
        arr.push({ href: this.homeInfo.excelUrl, label: 'SPREADSHEET' })
      }
      return arr
    },

    /** 菜单 index -> 导航项，供选中与跳转 */
    navIndexMap() {
      const map = {}
      const walk = (items, prefix) => {
        items.forEach((item, i) => {
          const idx = prefix === '' ? String(i) : `${prefix}-${i}`
          map[idx] = item
          if (Array.isArray(item.children) && item.children.length) {
            walk(item.children, idx)
          }
        })
      }
      walk(this.navList, '')
      return map
    },

    /** 当前路由对应菜单 index */
    activeNavIndex() {
      const entries = Object.entries(this.navIndexMap)
      for (let i = entries.length - 1; i >= 0; i -= 1) {
        const [idx, item] = entries[i]
        if (Array.isArray(item.children) && item.children.length) continue
        if (this.isNavActive(item)) return idx
      }
      return ''
    }
  },

  mounted() {
    this.$nextTick(() => {
      this.bindNavScroll()
      this.updateNavScrollArrows()
    })
  },
  watch: {
    expertId: {
      immediate: true,
      handler(val) {
        if (val) {
          this.fetchGoodsCategoryList()
        } else {
          this.state.goodsCategoryList = []
        }
      }
    },

    defaultMenuList: {
      immediate: true,
      deep: true,
      handler(val) {
        this.fetchHomeMenu()
      }
    },

    'state.drawerOpen'(open) {
      this.syncBodyScrollLock()
      if (open) this.$nextTick(() => this.syncDrawerNavBars())
    },
    'state.subDrawerOpen'(open) {
      this.syncBodyScrollLock()
      if (open) this.$nextTick(() => this.syncDrawerNavBars())
    },
    navList() {
      this.$nextTick(() => {
        this.updateNavScrollArrows()
        this.syncDrawerNavBars()
      })
    },
    'state.subNavItem'() {
      this.$nextTick(() => this.syncDrawerNavBars())
    }
  },
  methods: {
    // 菜单接口跳转：去掉 categoryName/cateIds/parentCateIds，仅保留 path 与其它 query
    normalizeMenuHref(href) {
      if (!href || href.startsWith('http')) return href
      try {
        const u = new URL(href, window.location.origin)
        u.searchParams.delete('categoryName')
        u.searchParams.delete('cateIds')
        u.searchParams.delete('parentCateIds')
        const q = u.searchParams.toString()
        return q ? `${u.pathname}?${q}` : u.pathname
      } catch (e) {
        const [path, qs] = href.split('?')
        if (!qs) return path
        const params = new URLSearchParams(qs)
        params.delete('categoryName')
        params.delete('cateIds')
        params.delete('parentCateIds')
        const q = params.toString()
        return q ? `${path}?${q}` : path
      }
    },

    // 菜单接口项 -> { href, label, children? }（不带 categoryName）
    mapMenuItem(item) {
      if (!item || typeof item !== 'object') return null
      const rawHref = String(item.url || item.href || '').trim()
      const label = String(item.menuName || item.label || '').trim()
      if (!rawHref || !label) return null

      const nav = {
        href: this.normalizeMenuHref(rawHref),
        label
      }
      return nav
    },

    // 解析 getHomeConfig.menuList（JSON 字符串或数组）
    parseMenuList(raw) {
      if (!raw) return []
      let list = raw
      if (typeof list === 'string') {
        try {
          list = JSON.parse(list)
        } catch (e) {
          return []
        }
      }
      if (!Array.isArray(list)) return []
      return list.map((item) => this.mapMenuItem(item)).filter(Boolean)
    },

    // 拉取首页顶部菜单配置
    async fetchHomeMenu() {
      if (this.isQequest) {
        try {
          const { data } = await productApi.getHomeConfig()
          this.state.menuList = this.parseMenuList(data?.menuList)
        } catch (e) {
          console.warn('[HomeTopbar] fetchHomeMenu failed', e)
          this.state.menuList = []
        }
      } else {
        this.state.menuList = this.parseMenuList(this.defaultMenuList || [])
      }
      this.$nextTick(() => this.updateNavScrollArrows())
    },

    // 拉取商品分类树
    async fetchGoodsCategoryList() {
      if (!this.expertId) return
      try {
        const res = await getGoodsCategoryList(this.expertId)
        this.state.goodsCategoryList = res.data || []
      } catch (e) {
        console.warn('[HomeTopbar] fetchGoodsCategoryList failed', e)
        this.state.goodsCategoryList = []
      }
      this.$nextTick(() => this.updateNavScrollArrows())
    },

    // 分类跳转：使用 parentCateIds 和 cateIds
    catNavHref(categoryId, parentCategoryId = null) {
      const params = new URLSearchParams()
      if (parentCategoryId) {
        params.set('parentCateIds', parentCategoryId)
      }
      if (categoryId) {
        params.set('cateIds', categoryId)
      }
      const qs = params.toString()
      return `/goods?${qs}`
    },

    // 一级分类；有二级且可展示则挂 children
    mapCatFirst(item) {
      if (!item?.categoryName) return null

      const nav = {
        label: item.categoryName,
        href: this.catNavHref(null, item.id),
        categoryId: item.id,
        categoryName: item.categoryName,
        categoryCover: item.categoryCover
      }

      const subs = (item.subCategoryList || [])

      if (subs.length) {
        nav.children = subs.map((sub) => ({
          label: sub.categoryName,
          href: this.catNavHref(sub.id, item.id),
          categoryId: sub.id,
          categoryName: sub.categoryName,
          categoryCover: sub.categoryCover
        }))
      }
      return nav
    },

    buildCatNavTree(list) {
      return (list || []).map((item) => this.mapCatFirst(item)).filter(Boolean)
    },

    navIdx(i, prefix = '') {
      return prefix === '' ? String(i) : `${prefix}-${i}`
    },

    hasNavChildren(item) {
      return Array.isArray(item?.children) && item.children.length > 0
    },

    isExtNav(href) {
      return typeof href === 'string' && href.startsWith('http')
    },

    isNavActive(item) {
      const href = item?.href
      if (!href || href.startsWith('http')) return false

      try {
        const u = new URL(href, window.location.origin)
        if (this.$route.path !== u.pathname) return false
        const itemQuery = Object.fromEntries(u.searchParams.entries())
        const keys = Object.keys(itemQuery)
        if (keys.length === 0) {
          return Object.keys(this.$route.query).length === 0
        }
        return keys.every((k) => String(this.$route.query[k] ?? '') === itemQuery[k])
      } catch (e) {
        return this.$route.path === href
      }
    },

    /** 顶栏横向菜单 DOM */
    getNavMenuEl() {
      const ref = this.$refs.navMenu
      return ref?.$el || ref || null
    },

    /** 菜单是否溢出、左右是否还能滚 */
    updateNavScrollArrows() {
      const el = this.getNavMenuEl()
      if (!el) {
        this.state.navCanScrollLeft = false
        this.state.navCanScrollRight = false
        return
      }
      const maxScroll = el.scrollWidth - el.clientWidth
      if (maxScroll <= 1) {
        this.state.navCanScrollLeft = false
        this.state.navCanScrollRight = false
        return
      }
      this.state.navCanScrollLeft = el.scrollLeft > 1
      this.state.navCanScrollRight = el.scrollLeft < maxScroll - 1
    },

    /** 点击箭头横向滚动菜单（px） */
    scrollNavMenu(delta) {
      const el = this.getNavMenuEl()
      if (!el) return
      el.scrollBy({ left: delta, behavior: 'smooth' })
      window.setTimeout(() => this.updateNavScrollArrows(), 320)
    },

    /** 绑定菜单 scroll / resize 以更新箭头 */
    bindNavScroll() {
      const el = this.getNavMenuEl()
      if (!el || this._navScrollBound) return
      this._navScrollBound = true
      this._onNavScroll = () => this.updateNavScrollArrows()
      el.addEventListener('scroll', this._onNavScroll, { passive: true })
      if (typeof ResizeObserver !== 'undefined') {
        this._navResizeObs = new ResizeObserver(() => this.updateNavScrollArrows())
        this._navResizeObs.observe(el)
      } else {
        this._onWinResize = () => this.updateNavScrollArrows()
        window.addEventListener('resize', this._onWinResize)
      }
    },

    unbindNavScroll() {
      const el = this.getNavMenuEl()
      if (el && this._onNavScroll) {
        el.removeEventListener('scroll', this._onNavScroll)
      }
      if (this._navResizeObs) {
        this._navResizeObs.disconnect()
        this._navResizeObs = null
      }
      if (this._onWinResize) {
        window.removeEventListener('resize', this._onWinResize)
        this._onWinResize = null
      }
      this._navScrollBound = false
    },

    onNavSelect(index) {
      const item = this.navIndexMap[index]
      if (!item?.href) return
      if (item.href.startsWith('http')) {
        window.open(item.href, '_blank')
        return
      }
      if (Array.isArray(item.children) && item.children.length) return
      // 已在当前路由时不再 push，避免 NavigationDuplicated
      if (this.isNavActive(item)) return
      this.$router.push(item.href)
    },
    onChangeExpert(platform) {
      this.$emit('change-expert', platform)
    },

    goGoodsSearch() {
      const raw = this.state.searchValue
      const keyword = (typeof raw === 'string' ? raw : String(raw ?? '')).trim()
      if (keyword.length <= 0) return
      this.closeAllDrawers()
      this.$router.push({
        path: '/goods',
        query: { keyword }
      })
    },

    /** 打开二级分类抽屉 */
    openSubDrawer(item) {
      if (!this.hasNavChildren(item)) return
      this.state.subNavItem = item
      this.state.subDrawerOpen = true
    },

    /** 关闭二级分类抽屉 */
    closeSubDrawer() {
      this.state.subDrawerOpen = false
    },

    /** 关闭主抽屉与二级抽屉 */
    closeAllDrawers() {
      this.state.subDrawerOpen = false
      this.state.drawerOpen = false
    },

    /** 主抽屉内链点击后关闭抽屉 */
    onDrawerNavClick() {
      this.closeAllDrawers()
    },

    /** 同步单个抽屉导航自定义滚动条 */
    syncDrawerNavBar(box) {
      if (!box) return
      const nav = box.querySelector('.t1-drawer-nav')
      const thumb = box.querySelector('.t1-drawer-thumb')
      if (!nav || !thumb) return

      const { scrollTop, scrollHeight, clientHeight } = nav
      const trackH = clientHeight
      if (scrollHeight <= clientHeight + 1) {
        thumb.style.display = 'none'
        return
      }

      thumb.style.display = 'block'
      const ratio = clientHeight / scrollHeight
      const thumbH = Math.max(32, Math.round(trackH * ratio))
      const maxTop = trackH - thumbH
      const scrollRange = scrollHeight - clientHeight
      const top = scrollRange > 0 ? maxTop * (scrollTop / scrollRange) : 0
      thumb.style.height = `${thumbH}px`
      thumb.style.top = `${top}px`
    },

    /** 同步主/二级抽屉导航滚动条 */
    syncDrawerNavBars() {
      this.syncDrawerNavBar(this.$refs.mainNavBox)
      this.syncDrawerNavBar(this.$refs.subNavBox)
    },

    /** 抽屉导航滚动：滑动时显示自定义滚动条，停止后隐藏 */
    onDrawerNavScroll(e) {
      const nav = e.currentTarget
      const box = nav && nav.closest ? nav.closest('.t1-drawer-nav-box') : null
      if (!box) return

      this.syncDrawerNavBar(box)
      box.classList.add('nav-scrolling')
      clearTimeout(box._drawerNavScrollTimer)
      box._drawerNavScrollTimer = setTimeout(() => {
        box.classList.remove('nav-scrolling')
      }, 600)
    },

    /** 抽屉打开时锁定页面滚动，避免穿透到背景与遮罩 */
    syncBodyScrollLock() {
      const locked = this.state.drawerOpen || this.state.subDrawerOpen
      if (locked) {
        if (this._prevBodyOverflow === undefined) {
          this._prevBodyOverflow = document.body.style.overflow
          this._prevHtmlOverflow = document.documentElement.style.overflow
        }
        document.body.style.overflow = 'hidden'
        document.documentElement.style.overflow = 'hidden'
        return
      }
      document.body.style.overflow = this._prevBodyOverflow || ''
      document.documentElement.style.overflow = this._prevHtmlOverflow || ''
      this._prevBodyOverflow = undefined
      this._prevHtmlOverflow = undefined
    }
  },
  beforeDestroy() {
    this.unbindNavScroll()
    document.body.style.overflow = this._prevBodyOverflow || ''
    document.documentElement.style.overflow = this._prevHtmlOverflow || ''
    this._prevBodyOverflow = undefined
    this._prevHtmlOverflow = undefined
  }
}
</script>

<style scoped lang="scss">
.section-wrap {
  width: min(1400px, 92vw);
  margin: 0 auto;
  padding: 1rem 4rem;
  padding-left: env(safe-area-inset-left, 0);
  padding-right: env(safe-area-inset-right, 0);
  display: -webkit-box;
  display: -webkit-flex;
  display: flex;
  -webkit-box-orient: horizontal;
  -webkit-box-direction: normal;
  -webkit-flex-direction: row;
  flex-direction: row;
  -webkit-box-pack: justify;
  -webkit-justify-content: space-between;
  justify-content: space-between;
  -webkit-box-align: center;
  -webkit-align-items: center;
  align-items: center;
}

.topbar {
  width: 100%;
  max-width: 2000px;
  padding: 0 !important;
  display: -webkit-box;
  display: -webkit-flex;
  display: flex;
  -webkit-box-orient: vertical;
  -webkit-box-direction: normal;
  -webkit-flex-direction: column;
  flex-direction: column;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 12;
  isolation: isolate;
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
  background: transparent;
}

.header-btn__wrap {
  width: 100%;
  background: linear-gradient(to right, rgb(255, 111, 0), rgb(255, 217, 0));
}

.header-btn {
  margin: 0px;
  width: 100%;
  padding: 10px 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  line-height: 1.2;
  color: #fff;
  font-size: 14px;
  font-family: 'Roboto Mono', monospace !important;
  text-align: center;
  transition: all .05s ease-in-out;

  .btn__icon {
    width: 18px;
    height: 18px;
  }
}

.header-btn:hover {
  color: #fff !important;
  transform: translateY(-1px) scale(1.01);
}

/* iOS Safari/WebView flex fallback */
@supports (-webkit-touch-callout: none) {
  .topbar {
    display: -webkit-box;
    display: -webkit-flex;
    display: flex;
  }
}

.topbar::before {
  content: "";
  position: absolute;
  inset: 0;
  left: 50%;
  width: 100vw;
  -webkit-transform: translateX(-50%);
  transform: translateX(-50%);
  z-index: -1;
  background: var(--background-color);
  border-bottom: 1px solid hsl(from var(--background-color) h s l / 0.09);
  box-shadow: 0 10px 22px hsl(from var(--background-color) h s l / 0.88);
}

@media (max-width: 1200px) {
  .section-wrap {
    padding: 1rem;
    width: 100%;
  }
}

.brand {
  font-family: "Anton", sans-serif;
  color: var(--select-color) !important;
  text-decoration: none;
  letter-spacing: 1.5px;
  font-size: clamp(2rem, 5vw, 2.6rem);
}

.brand:hover {
  color: var(--primary-color) !important;
}

/* el-menu 勿套用 flex，避免与水平菜单布局冲突 */
.t1-nav:not(.t1-menu) {
  display: -webkit-box;
  display: -webkit-flex;
  display: flex;
  gap: 1.3rem;
}

/* 旧 nav 链接样式；el-menu 内统一走 .nav-btn / .t1-menu */
.t1-nav:not(.t1-menu) a {
  font-family: 'Roboto Mono', monospace;
  color: hsl(from var(--primary-color) h s l / 0.6) !important;
  text-decoration: none;
  text-transform: uppercase;
  font-size: 1rem;
  letter-spacing: 0.07em;
  font-weight: 600;
}

.t1-nav:not(.t1-menu) a:hover {
  color: var(--primary-color) !important;
}

.topbar-desktop {
  flex: 1;
  -webkit-box-flex: 1;
  -webkit-flex: 1;
  display: -webkit-box;
  display: -webkit-flex;
  display: flex;
  -webkit-box-align: center;
  -webkit-align-items: center;
  align-items: center;
  -webkit-justify-content: center;
  -webkit-justify-content: center;
  justify-content: center;
  gap: 1.5rem;
}

/* 与 SPREADSHEET 外链一致的 nav 字色与字重 */
.nav-btn {
  color: hsl(from var(--primary-color) h s l/.6) !important;
  text-decoration: none;
  text-transform: uppercase;
  font-size: 1rem;
  letter-spacing: 0.07em;
  font-weight: 900 !important;
}

.nav-btn:hover {
  color: var(--primary-color) !important;
}

/* 水平 el-menu：勿用 flex 覆盖默认布局，否则子菜单 popper 无法弹出 */
:deep(.t1-menu.el-menu--horizontal) {
  border-bottom: none;
  background: transparent;
  -webkit-justify-content: flex-start;
  justify-content: flex-start;
}

/* 顶栏菜单横向滚动容器 */
.nav-scroll-wrap {
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
  margin-left: 20px;
  display: -webkit-box;
  display: -webkit-flex;
  display: flex;
  -webkit-box-align: center;
  -webkit-align-items: center;
  align-items: center;
}

.nav-scroll-btn {
  position: absolute;
  top: 50%;
  z-index: 2;
  display: -webkit-box;
  display: -webkit-flex;
  display: flex;
  -webkit-box-align: center;
  -webkit-align-items: center;
  align-items: center;
  -webkit-box-pack: center;
  -webkit-justify-content: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  cursor: pointer;
  color: var(--primary-color);
  background: var(--background-color);
  -webkit-transform: translateY(-50%);
  transform: translateY(-50%);
  box-shadow: 0 0 8px var(--background-color);
}

.nav-scroll-btn:hover {
  color: hsl(from var(--primary-color) h s l / 0.75);
}

.nav-scroll-l {
  left: 0;
}

.nav-scroll-r {
  right: 0;
}

/* 顶栏导航：左对齐 + 横向滚动 */
.nav-scroll-wrap .t1-menu {
  flex: 1 1 auto;
  width: 100%;
  min-width: 0;
  -webkit-justify-content: flex-start;
  justify-content: flex-start;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.nav-scroll-wrap.nav-scroll-pad .t1-menu {
  padding-left: 28px;
  padding-right: 28px;
}

.nav-scroll-wrap .t1-menu::-webkit-scrollbar {
  width: 0 !important;
  height: 0 !important;
  display: none;
}

:deep(.t1-menu.el-menu--horizontal > .el-menu-item),
:deep(.t1-menu.el-menu--horizontal > .el-submenu) {
  margin-right: 1.3rem;
}

:deep(.t1-menu.el-menu--horizontal > .el-submenu:last-child),
:deep(.t1-menu.el-menu--horizontal > .el-menu-item:last-child) {
  margin-right: 0;
}

:deep(.t1-menu.el-menu--horizontal > .el-menu-item),
:deep(.t1-menu.el-menu--horizontal > .el-submenu > .el-submenu__title),
:deep(.t1-menu.el-menu--horizontal > .el-menu-item.is-active),
:deep(.t1-menu.el-menu--horizontal > .el-submenu.is-active > .el-submenu__title) {
  height: auto;
  line-height: inherit;
  padding: 0;
  margin: 0;
  border-bottom: none !important;
  background: transparent !important;
  color: hsl(from var(--primary-color) h s l/.6) !important;
  text-decoration: none;
  text-transform: uppercase;
  font-size: 1rem;
  letter-spacing: 0.07em;
  font-weight: 900;
}

:deep(.t1-menu.el-menu--horizontal > .el-menu-item:hover),
:deep(.t1-menu.el-menu--horizontal > .el-menu-item.is-active:hover),
:deep(.t1-menu.el-menu--horizontal > .el-submenu:hover > .el-submenu__title),
:deep(.t1-menu.el-menu--horizontal > .el-submenu > .el-submenu__title:hover) {
  color: var(--primary-color) !important;
  background: transparent !important;
}

:deep(.t1-menu.el-menu--horizontal > .el-submenu .el-submenu__icon-arrow) {
  position: static;
  margin-top: 0;
  margin-left: 0.25rem;
  font-size: 1rem;
  color: inherit;
  vertical-align: middle;
  font-weight: 900;
}

:deep(.t1-menu .el-menu-item a.nav-btn) {
  display: inline-block;
  font-weight: 600;
}

.topbar-logo {
  height: 40px;
  width: auto;
  object-fit: contain;
}

.topbar-mobile-btn {
  display: none;
  -webkit-box-align: center;
  -webkit-align-items: center;
  align-items: center;
  -webkit-box-pack: center;
  -webkit-justify-content: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border: 1px solid var(--select-border-color);
  border-radius: 6px;
  background: transparent;
  color: var(--select-color);
  font-size: 1.1rem;
  cursor: pointer;
}

.topbar-mobile-btn:hover,
.topbar-mobile-btn:focus {
  color: var(--primary-color) !important;
  border: 1px solid var(--primary-color) !important;
}

.t1-drawer-inner {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  overscroll-behavior: contain;
  padding: 12px 4px 16px 16px;
  background: var(--background-color);
}

.t1-drawer-head {
  display: -webkit-box;
  display: -webkit-flex;
  display: flex;
  -webkit-box-align: center;
  -webkit-align-items: center;
  align-items: center;
  -webkit-box-pack: justify;
  -webkit-justify-content: space-between;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 1.2rem;
}

.t1-drawer-close {
  border: 0;
  background: transparent;
  color: var(--select-color) !important;
  font-size: 1.45rem;
  cursor: pointer;
}

.t1-drawer-nav-box {
  position: relative;
  flex: 1;
  min-height: 0;
  margin-bottom: 20px;
}

.t1-drawer-nav {
  height: 100%;
  min-height: 0;
  padding-right: 10px;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;
  display: -webkit-box;
  display: -webkit-flex;
  display: flex;
  -webkit-box-orient: vertical;
  -webkit-flex-direction: column;
  flex-direction: column;
  gap: 0.1rem;
}

.t1-drawer-nav::-webkit-scrollbar {
  width: 0;
  height: 0;
  display: none;
}

/* 自定义滚动条：圆角由 div 绘制，避免 WebKit 原生滑块无圆角 */
.t1-drawer-bar {
  position: absolute;
  top: 0;
  right: 0;
  width: 6px;
  height: 100%;
  background: hsl(from var(--select-color) h s l / 0.35);
  border-radius: 20px;
  opacity: 0;
  pointer-events: none;
  overflow: hidden;
  -webkit-transition: opacity 0.2s;
  transition: opacity 0.2s;
}

.t1-drawer-nav-box.nav-scrolling .t1-drawer-bar {
  opacity: 1;
}

.t1-drawer-thumb {
  position: absolute;
  left: 0;
  width: 6px;
  background: hsl(from var(--select-color) h s l / 0.55);
  border-radius: 20px;
  min-height: 32px;
}

.t1-drawer-nav-item {
  display: block;
}

/* 有子级时用 button，样式与链接一致 */
button.t1-drawer-link {
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  text-align: left;
}

.t1-drawer-link {
  width: 100%;
  display: -webkit-inline-box;
  display: -webkit-inline-flex;
  display: inline-flex;
  -webkit-box-align: center;
  -webkit-align-items: center;
  align-items: center;
  -webkit-justify-content: space-between;
  justify-content: space-between;
  gap: 0.3rem;
  color: var(--select-color) !important;
  text-decoration: none;
  text-transform: uppercase;
  font-family: "Anton", sans-serif;
  letter-spacing: 0.02em;
  font-size: clamp(1.4rem, 9vw, 1.4rem);
  line-height: 2.5;
}

.t1-drawer-link-img {
  width: 24px;
  height: 24px;
  margin-right: 8px;
  object-fit: contain;
}

.t1-sub-drawer .t1-drawer-link {
  padding: 0 16px;
  margin-bottom: 11px;
  border: 1px solid hsl(from var(--border-color) h s l / 0.5);
  border-radius: 10px;
  line-height: 40px;
  -webkit-justify-content: flex-start;
  justify-content: flex-start;
}

.t1-sub-head {
  display: -webkit-box;
  display: -webkit-flex;
  display: flex;
  -webkit-box-align: center;
  -webkit-align-items: center;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 1.2rem;
}

.t1-sub-back {
  border: 0;
  background: transparent;
  color: var(--select-color) !important;
  font-size: 1.45rem;
  cursor: pointer;
  padding: 0;
  line-height: 1;
}

.t1-sub-title {
  color: var(--select-color);
  text-transform: uppercase;
  font-family: "Anton", sans-serif;
  letter-spacing: 0.02em;
  font-size: clamp(1.6rem, 7vw, 2.2rem);
  line-height: 1.3;
}

.t1-drawer-no {
  margin-top: 0.2rem;
  font-style: normal;
  font-family: "JetBrains Mono", ui-monospace, monospace;
  font-size: 0.72rem;
  letter-spacing: 0.02em;
}

:deep(.t1-mobile-drawer) {
  background: var(--primary-color);
  overflow: hidden;
  overscroll-behavior: contain;
}

:deep(.t1-mobile-drawer .el-drawer__body) {
  padding: 0;
  height: 100%;
  overflow: hidden;
  overscroll-behavior: contain;
}

@media (max-width: 980px) {
  .topbar-desktop {
    display: none !important;
  }

  .brand {
    flex: 1 !important;
    font-size: clamp(1.35rem, 8vw, 2rem);
  }

  .topbar-logo {
    height: 32px;
    width: auto;
  }

  .topbar-mobile-btn {
    display: -webkit-inline-box;
    display: -webkit-inline-flex;
    display: inline-flex;
    margin-left: 20px;
  }
}

:deep(.el-input__inner) {
  color: var(--select-color) !important;
  border: 1px solid hsl(from var(--select-border-color) h s l / 0.8) !important;
  background-color: hsl(from var(--select-border-color) h s l / 0.5);
  border-radius: 12px;
}

.platform-wrap {
  display: -webkit-box;
  display: -webkit-flex;
  display: flex;
  -webkit-box-align: center;
  -webkit-align-items: center;
  align-items: center;
  gap: 16px;
}

.topbar__platform-wrap :deep(.platform-left) {
  margin: 0;
}
</style>

<style>
/* append-to-body，需全局样式拦截滚动链 */
.el-drawer__wrapper:has(.t1-mobile-drawer) {
  overscroll-behavior: contain;
}

.t1-mobile-drawer {
  overscroll-behavior: contain;
}

.t1-mobile-drawer .el-drawer__body {
  height: 100%;
  overflow: hidden;
  overscroll-behavior: contain;
}

/* append-to-body：隐藏原生滚动条，使用自定义圆角滚动条 */
.t1-mobile-drawer .t1-drawer-nav-box {
  position: relative;
  flex: 1;
  min-height: 0;
  margin-bottom: 20px;
}

.t1-mobile-drawer .t1-drawer-nav {
  height: 100%;
  min-height: 0;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.t1-mobile-drawer .t1-drawer-nav::-webkit-scrollbar {
  width: 0 !important;
  height: 0 !important;
  display: none;
}

.t1-mobile-drawer .t1-drawer-bar {
  position: absolute;
  top: 0;
  right: 0;
  width: 6px;
  height: 100%;
  background: hsl(from var(--select-color) h s l / 0.35);
  border-radius: 20px;
  opacity: 0;
  pointer-events: none;
  overflow: hidden;
  transition: opacity 0.2s;
}

.t1-mobile-drawer .t1-drawer-nav-box.nav-scrolling .t1-drawer-bar {
  opacity: 1;
}

.t1-mobile-drawer .t1-drawer-thumb {
  position: absolute;
  left: 0;
  width: 6px;
  background: hsl(from var(--select-color) h s l / 0.55);
  border-radius: 20px;
  min-height: 32px;
}

.menu-popup__wrap {
  z-index: 2000;
}

/* PC 分类下拉：最高 50vh，超出滚动并隐藏滚动条（append-to-body 需全局样式） */
.menu-popup__wrap .el-menu--popup {
  max-height: 50vh;
  overflow-y: auto;
  overflow-x: hidden;
  /* 仅限制本区域滚动，不链式带动页面 */
  overscroll-behavior-y: contain;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.menu-popup__wrap .el-menu--popup::-webkit-scrollbar {
  width: 0 !important;
  height: 0 !important;
  display: none;
}

.menu-popup__wrap .el-menu-item,
.menu-popup__wrap .el-submenu__title {
  background-color: var(--background-color) !important;
}

.menu-popup__wrap .el-menu-item:hover,
.menu-popup__wrap .el-submenu__title:hover {
  color: var(--primary-color) !important;
  background-color: var(--background-color) !important;
}
</style>
